type ChapterSequenceWithPosition = {
  position?: number | null;
  sequence?: { id?: number | string };
};

export const renumberChapterSequencePositions = <
  T extends ChapterSequenceWithPosition
>(
  chapterSequences: T[]
): T[] =>
  chapterSequences.map((chapterSequence, arrayIndex) => ({
    ...chapterSequence,
    position: arrayIndex + 1
  }));

type ChapterWithSequences = {
  id?: number | string;
  chapterSequences: { id?: number | string }[];
};

type SequenceDropContext = {
  chapters: ChapterWithSequences[];
  draggedChapterSequenceId?: number | string;
  draggedChapterId?: number | string;
  dropTargetChapterSequenceId?: number | string;
  dropTargetIndex: number;
  dropTargetChapterId?: number | string;
};

const sameIdentifier = (left?: number | string, right?: number | string) =>
  String(left) === String(right);

const isDroppingBelowTarget = ({
  chapters,
  draggedChapterSequenceId,
  dropTargetChapterSequenceId
}: SequenceDropContext) => {
  const allSequences = chapters.flatMap(chapter => chapter.chapterSequences);
  const draggedIndex = allSequences.findIndex(chapterSequence =>
    sameIdentifier(chapterSequence.id, draggedChapterSequenceId)
  );
  const dropTargetPosition = allSequences.findIndex(chapterSequence =>
    sameIdentifier(chapterSequence.id, dropTargetChapterSequenceId)
  );

  return dropTargetPosition > draggedIndex;
};

export const resolveSequenceDropIndex = (
  dropContext: SequenceDropContext
): number => {
  const { draggedChapterId, dropTargetChapterId, dropTargetIndex } =
    dropContext;

  if (sameIdentifier(draggedChapterId, dropTargetChapterId)) {
    return dropTargetIndex;
  }

  return isDroppingBelowTarget(dropContext)
    ? dropTargetIndex + 1
    : dropTargetIndex;
};

export const positionAfterSequence = (
  chapterSequences: ChapterSequenceWithPosition[],
  sequenceId?: number | string
): number => {
  const currentIndex = chapterSequences.findIndex(
    chapterSequence => chapterSequence.sequence?.id === sequenceId
  );

  if (currentIndex === -1) {
    return chapterSequences.length + 1;
  }

  return currentIndex + 2;
};
